import {Tag, Tooltip} from 'antd';
import PropTypes from 'prop-types';
import styles from './walletTxType.module.scss';

/**
 * What a wallet transaction is, and which way the money moved.
 *
 * These are two different fields and they were being shown as one. The
 * platform table rendered `direction` under a column headed "Type"; the user's
 * wallet table rendered `type` under a column headed "Direction". So every row
 * was mislabelled on one screen or the other, and a swap in particular read as
 * "CURRENCY SWAP" with nothing to say whether the money left this wallet or
 * arrived in it.
 *
 * `type` is what happened (a deposit, a buy order, a currency swap).
 * `direction` is only ever DEPOSIT or WITHDRAWAL — money in or money out of
 * the ledger you are looking at.
 */

/** Colour per transaction type. Anything unlisted falls back to blue. */
const TYPE_COLORS = {
  DEPOSIT: 'black',
  WITHDRAWAL: 'default',
  STOCK_BUY: 'magenta',
  STOCK_SELL: 'cyan',
  BUY_ORDER: 'magenta',
  SELL_ORDER: 'cyan',
  DIVIDEND_PAYOUT: 'orange',
  DIVIDEND_REVERSAL: 'red',
  CURRENCY_SWAP: 'geekblue',
  VENTURE_INVESTMENT: 'purple',
  VENTURE_FUND_INVESTMENT: 'gold',
  BOND_INVESTMENT: 'volcano',
  REWARDS_PAYIN: 'lime',
};

/** Never show a raw enum. */
export const humanizeEnum = value =>
  String(value || '')
    .toLowerCase()
    .split('_')
    .filter(Boolean)
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');

export const isSwap = record =>
  record?.type === 'CURRENCY_SWAP' || Boolean(record?.swap);

/**
 * The swap as seen from the ledger being viewed.
 *
 * A swap writes one transaction into each currency's ledger — a WITHDRAWAL in
 * the source currency and a DEPOSIT in the target — and both carry the same
 * `swap` object, so the pair alone cannot say which leg a row is. `direction`
 * is what distinguishes them.
 *
 * Returns null when the row is not a swap, or when the service sent no swap
 * payload (older records predate it, and the pair cannot be invented).
 */
export const readSwapLeg = record => {
  if (!isSwap(record)) return null;

  const from = record?.swap?.sourceCurrency?.currency;
  const to = record?.swap?.targetCurrency?.currency;
  if (!from || !to) return null;

  // DEPOSIT means the money landed here, so this ledger is the target.
  const incoming = record?.direction === 'DEPOSIT';

  return {
    from,
    to,
    incoming,
    // Named from the operator's point of view — they are looking at one
    // wallet and want to know where the money went or came from, not the
    // swap's own source/target.
    counterCurrency: incoming ? from : to,
    swapId: record?.swap?.id,
  };
};

/**
 * The Type cell: what happened, plus the pair for a swap.
 *
 * A swap shows `USD → NGN` rather than the bare word, because the pair is the
 * one thing an operator cannot recover from the row otherwise — the ledger
 * column only tells them the currency they are already looking at.
 */
export const WalletTxTypeCell = ({record, onOpenSwap}) => {
  const leg = readSwapLeg(record);
  const label = humanizeEnum(record?.type) || '—';
  const tag = (
    <Tag className={styles.typeTag} color={TYPE_COLORS[record?.type] || 'blue'}>
      {label}
    </Tag>
  );

  if (!leg) return tag;

  const pair = (
    <span className={styles.pair}>
      <span className={leg.incoming ? styles.muted : styles.strong}>
        {leg.from}
      </span>
      <span className={styles.arrow}>→</span>
      <span className={leg.incoming ? styles.strong : styles.muted}>
        {leg.to}
      </span>
    </span>
  );

  return (
    <span className={styles.cell}>
      {tag}
      {onOpenSwap && leg.swapId ? (
        <Tooltip title="Open this swap, with both legs">
          <button
            type="button"
            className={styles.pairLink}
            onClick={event => {
              // The row itself opens a transaction drawer on click.
              event.stopPropagation();
              onOpenSwap(leg);
            }}>
            {pair}
          </button>
        </Tooltip>
      ) : (
        pair
      )}
    </span>
  );
};

WalletTxTypeCell.propTypes = {
  record: PropTypes.object,
  onOpenSwap: PropTypes.func,
};

/**
 * The Direction cell: money in or money out of this ledger.
 *
 * Deliberately worded as In / Out rather than Deposit / Withdrawal. On a swap
 * row "Withdrawal" reads as though the user withdrew to a bank, when all that
 * happened is the money left this currency for another one.
 */
export const WalletTxDirectionCell = ({record}) => {
  const incoming = record?.direction === 'DEPOSIT';
  const outgoing = record?.direction === 'WITHDRAWAL';
  if (!incoming && !outgoing) {
    return (
      <span className={styles.muted}>{humanizeEnum(record?.direction)}</span>
    );
  }

  const leg = readSwapLeg(record);

  return (
    <Tooltip
      title={
        leg
          ? incoming
            ? `Swapped in from ${leg.counterCurrency}`
            : `Swapped out to ${leg.counterCurrency}`
          : incoming
          ? 'Money into this wallet'
          : 'Money out of this wallet'
      }>
      <Tag className={styles.dirTag} color={incoming ? 'green' : 'volcano'}>
        {incoming ? '↓ In' : '↑ Out'}
      </Tag>
    </Tooltip>
  );
};

WalletTxDirectionCell.propTypes = {
  record: PropTypes.object,
};
